import axios from "axios";
import { toast } from "react-toastify";
import NotificationMessage from "../../../components/core/NotificationMessage";

// Redux
import { store } from "../../store";
import { userActions } from "../../actions/userActions";

// Constants
import {
  AUTHENTICATION_FAILED,
  UNEXPECTED_SERVER_ERROR,
  SERVICE_ERROR,
  TECHNICAL_FAILURE,
  ACCESS_DENIED,
} from "../../../constants/messageConstants";

// Helper
import { config as appConfig } from "../config";
import {
  BACKEND_BEFORE_IDLE_MILLISECONDS,
  WARNING_BEFORE_IDLE_MILLISECONDS,
} from "../../../constants/userConstants";

let logoutWarningTimer = null;
let backendActivityCheckTimer = null;
let isActivityListenersAttached = false;
let lastUserActivityEventTime = 0;

function getSessionTimeoutConfig() {
  const defaultWarningBeforeIdleInMilliseconds =
    WARNING_BEFORE_IDLE_MILLISECONDS;
  const sessionExpiryTimeInMinutes = Number(
    localStorage.getItem("sessionExpiryTimeInMinutes")
  );
  const defaultIdleTimeInMilliseconds = sessionExpiryTimeInMinutes * 60 * 1000;
  const sessionExpiryWarningTime =
    defaultIdleTimeInMilliseconds - defaultWarningBeforeIdleInMilliseconds;

  const backendExpiryBeforeIdleInMilliseconds =
    defaultIdleTimeInMilliseconds - BACKEND_BEFORE_IDLE_MILLISECONDS;

  return {
    defaultIdleTimeInMilliseconds,
    sessionExpiryWarningTime,
    backendExpiryBeforeIdleInMilliseconds,
    timerDelayInMilliseconds: Math.max(sessionExpiryWarningTime, 0),
  };
}

function updateUserLastActivityTime() {
  clearTimeout(logoutWarningTimer);
  const currentActivityTime = Date.now();
  localStorage.setItem("lastActivityTime", String(currentActivityTime));
}

function updateBackendLastActivityTime() {
  const currentActivityTime = Date.now();
  localStorage.setItem("lastBackendActivityTime", String(currentActivityTime));
}

function setLogoutWarningTimer() {
  clearInterval(logoutWarningTimer);
  const { backendExpiryBeforeIdleInMilliseconds } = getSessionTimeoutConfig();

  // Separate timer to check backend activity independently
  if (backendActivityCheckTimer === null) {
    backendActivityCheckTimer = setInterval(() => {
      const lastBackendActivityTimeMs =
        localStorage.getItem("lastBackendActivityTime") &&
        Number(localStorage.getItem("lastBackendActivityTime"));
      const lastUserActivityTimeMs =
        localStorage.getItem("lastActivityTime") &&
        Number(localStorage.getItem("lastActivityTime"));

      if (lastBackendActivityTimeMs) {
        const idleBackendActivityTime = Date.now() - lastBackendActivityTimeMs;
        const idleUserTime = lastUserActivityTimeMs
          ? Date.now() - lastUserActivityTimeMs
          : Number.MAX_SAFE_INTEGER;

        console.log(
          "idleBackendActivityTime : ",
          idleBackendActivityTime,
          " In minutes : " + idleBackendActivityTime / 60000
        );

        console.log(
          "idleUserTime : ",
          idleUserTime,
          " In minutes : " + idleUserTime / 60000
        );

        // If backend has been idle beyond warning threshold, call keep-alive API
        if (
          idleBackendActivityTime > backendExpiryBeforeIdleInMilliseconds &&
          idleUserTime < idleBackendActivityTime
        ) {
          console.log("keep-alive API");
          store.dispatch(userActions.getSessionTimeExtended());
        }
      }
    }, 30000); // Check every 30 seconds
  }

  if (logoutWarningTimer !== null) {
    store.dispatch(userActions.showLogoutWarningTimer(false));
    clearInterval(logoutWarningTimer);
  }

  // User inactivity timeout - based on user activity alone
  const { timerDelayInMilliseconds } = getSessionTimeoutConfig();
  logoutWarningTimer = setInterval(() => {
    store.dispatch(userActions.showLogoutWarningTimer(true));
    // Notify other tabs to show the warning popup too
    new BroadcastChannel("mdc_auth").postMessage({ type: "SESSION_WARNING" });
  }, timerDelayInMilliseconds);
}

function resetLogoutWarningTimerFromActivity() {
  const user = localStorage.getItem("user");

  if (!user) {
    return;
  }

  clearInterval(logoutWarningTimer);

  setLogoutWarningTimer();
}

function attachUserActivityListeners() {
  if (isActivityListenersAttached) {
    return;
  }

  const handleUserActivity = () => {
    const now = Date.now();

    if (now - lastUserActivityEventTime < 1000) {
      return;
    }

    lastUserActivityEventTime = now;

    // Only reset timers if warning hasn't been shown yet
    if (!store.getState()?.authentication?.showSessionTimeoutWarning) {
      updateUserLastActivityTime();
      clearTimeout(logoutWarningTimer);
      resetLogoutWarningTimerFromActivity();
    }
  };

  window.addEventListener("mousemove", handleUserActivity);
  window.addEventListener("click", handleUserActivity);
  window.addEventListener("keydown", handleUserActivity);
  isActivityListenersAttached = true;
}

export default function interceptor() {
  let user = localStorage.getItem("user");

  axios.defaults.withCredentials = true;
  if (Object.keys(user || {}).length > 0) {
    if (!localStorage.getItem("lastActivityTime")) {
      updateUserLastActivityTime();
    }
    if (!localStorage.getItem("lastBackendActivityTime")) {
      updateBackendLastActivityTime();
    }

    setLogoutWarningTimer();
    attachUserActivityListeners();
  }

  axios.interceptors.request.use(
    (config) => {
      // Redirect immediately if session is already cleared (e.g., logout from another tab)
      // to prevent sending unauthorized API requests.
      const isPublicEndpoint = config.url?.includes("/application/version");
      if (
        !localStorage.getItem("user") &&
        !isPublicEndpoint &&
        window.location.pathname !== "/login"
      ) {
        sessionStorage.setItem("isSessionTimedout", "true");
        window.location.replace("/login");
        return Promise.reject(new axios.Cancel("Session expired"));
      }

      config.headers.common.accept = "application/json";
      config.headers.common["X-Requested-With"] = "XMLHttpRequest";
      const activeUser = JSON.parse(localStorage.getItem("activeUser"));
      const sessionUser = JSON.parse(sessionStorage.getItem("activeUser"));
      const isTenantSwitched = sessionStorage.getItem("isTenantSwitched");

      if (sessionUser && !isTenantSwitched) {
        config.headers.common["X-Group-ID"] = sessionUser?.groupID;
        config.headers.common["X-Tenant-Alias"] = sessionUser?.value;
      } else if (activeUser) {
        config.headers.common["X-Group-ID"] = activeUser?.groupID;
        config.headers.common["X-Tenant-Alias"] = activeUser?.value;
      }
      //will update these conditions once GroupId and TenantAlias got in sync
      if ("GroupId" in config.headers && "TenantAlias" in config.headers) {
        config.headers.common["X-Group-ID"] = config.headers.GroupId;
        config.headers.common["X-Tenant-Alias"] = config.headers.TenantAlias;
      } else if (
        "Groupid" in config.headers &&
        "Tenantalias" in config.headers
      ) {
        config.headers.common["X-Group-ID"] = config.headers.Groupid;
        config.headers.common["X-Tenant-Alias"] = config.headers.Tenantalias;
      } else if (
        "groupId" in config.headers &&
        "tenantAlias" in config.headers
      ) {
        config.headers.common["X-Group-ID"] = config.headers.groupId;
        config.headers.common["X-Tenant-Alias"] = config.headers.tenantAlias;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  axios.interceptors.response.use(
    (response) => {
      // Handle expired session when the backend returns an OAuth/login HTML page
      // instead of the expected JSON response.
      const contentType = response?.headers?.["content-type"] || "";
      if (
        contentType.includes("text/html") &&
        response?.config?.headers?.common?.accept === "application/json"
      ) {
        // Skip if already on login page to prevent infinite loop
        if (window.location.pathname === "/login") {
          return Promise.reject(new Error("Session expired"));
        }
        // Session expired - backend returned OAuth page instead of JSON
        new BroadcastChannel("mdc_auth").postMessage({
          type: "SESSION_EXPIRED",
        });
        localStorage.clear();
        sessionStorage.clear();
        sessionStorage.setItem("isSessionTimedout", "true");
        window.location.replace(`${appConfig.apiUrl}/users/kclogout`);
        return Promise.reject(new Error("Session expired"));
      }
      let user = localStorage.getItem("user");

      let pathname = "";

      if (response?.config?.url) {
        pathname = new URL(response.config.url).pathname;
      }
      // Dont include the specific url
      const NOT_ALLOWED_URL = [
        "/notifications/token-for-websocket",
        "/notifications/ws",
      ];

      if (user) {
        if (!NOT_ALLOWED_URL.includes(pathname)) {
          // Update backend activity time on successful API response
          updateBackendLastActivityTime();
        }
      }
      return response;
    },
    async (error) => {
      let errorDetails = { ...error };
      let errorMessage;
      const isErrorJsonType =
        error?.response?.data instanceof Blob &&
        error?.response?.data?.type === "application/json";

      // If there's no response (network error, CORS failure from OAuth redirect),
      // treat as session expired and redirect to login
      if (!errorDetails?.response) {
        // Do not logout for network/CORS errors
        if (error.code === "ERR_NETWORK" || error.message === "Network Error") {
          return Promise.reject({ ...error });
        }

        // Existing session timeout logic
        if (
          !localStorage.getItem("user") ||
          window.location.pathname === "/login"
        ) {
          return Promise.reject({ ...error });
        }
        
        new BroadcastChannel("mdc_auth").postMessage({
          type: "SESSION_EXPIRED",
        });
        localStorage.clear();
        sessionStorage.clear();
        sessionStorage.setItem("isSessionTimedout", "true");
        window.location.replace(`${appConfig.apiUrl}/users/kclogout`);
        return Promise.reject({ ...error });
      }

      switch (errorDetails?.response?.status) {
        case 400: //Invalid credentioals
          let data;
          if (isErrorJsonType) {
            data = JSON.parse(await error.response.data.text());
          } else {
            data = errorDetails.response.data;
          }
          errorMessage =
            data && data.message ? data.message : AUTHENTICATION_FAILED;

          // Do not show toast for CSV import bad request error
          const VORGANS_CSV_IMPORT_PATH = "/vorgangs/csv-import";
          const { pathname } = new URL(errorDetails.config?.url);
          if (!(pathname === VORGANS_CSV_IMPORT_PATH)) {
            toast.error(<NotificationMessage message={errorMessage} />);
          }
          break;
        case 401: //token expired
          if (logoutWarningTimer !== null) {
            clearTimeout(logoutWarningTimer);
            logoutWarningTimer = null;
          }
          if (backendActivityCheckTimer !== null) {
            clearInterval(backendActivityCheckTimer);
            backendActivityCheckTimer = null;
          }
          // Clear Local Storage
          new BroadcastChannel("mdc_auth").postMessage({
            type: "SESSION_EXPIRED",
          });
          localStorage.clear();
          // Clear Session Storage
          sessionStorage.clear();
          sessionStorage.setItem("isSessionTimedout", "true");
          // Full page redirect to kill all running async operations and end Keycloak session
          window.location.replace(`${appConfig.apiUrl}/users/kclogout`);
          /* eslint no-restricted-globals:0 */
          break;
        case 500: //server error
          if (isErrorJsonType) {
            data = JSON.parse(await error.response.data.text());
          } else {
            data = errorDetails.response.data;
          }
          errorMessage =
            data && data.message ? data.message : UNEXPECTED_SERVER_ERROR;
          toast.error(<NotificationMessage message={errorMessage} />);
          break;
        case 403:
          toast.error(<NotificationMessage message={ACCESS_DENIED} />);
          break;
        case 503: //Service Unavailable
          if (isErrorJsonType) {
            data = JSON.parse(await error.response.data.text());
          } else {
            data = errorDetails.response.data;
          }
          errorMessage = data && data.message ? data.message : SERVICE_ERROR;
          toast.error(<NotificationMessage message={errorMessage} />);
          break;
        default:
          if (isErrorJsonType) {
            data = JSON.parse(await error.response.data.text());
          } else {
            data = errorDetails.response.data;
          }
          errorMessage =
            data && data.message ? data.message : TECHNICAL_FAILURE;
          toast.error(<NotificationMessage message={errorMessage} />); // Default error if we are not getting status text from backend
      }
      return Promise.reject({ ...error });
    }
  );
}
